import React, { useEffect, useState } from 'react';
import Cookie from 'js-cookie';
import { BASE_URL } from '../../config/constants';
import { useTranslation } from 'react-i18next';

const AddTags = () => {
	const { t } = useTranslation();
	const [tag, setTag] = useState('');
	const [tags, setTags] = useState([]);
	const [deletedTagId, setDeletedTagId] = useState('');
	const [mediaFile, setMediaFile] = useState(null);
	const [uploadedMediaId, setUploadedMediaId] = useState('');
	const [isUploading, setIsUploading] = useState(false);
	const baseUrl = BASE_URL;

	const getTags = async () => {
		try {
			const response = await fetch(`${baseUrl}/api/v1/tags`, {
				method: 'GET',
				headers: {
					Authorization: `Bearer ${Cookie.get('token')}`,
				},
			});

			const data = await response.json();

			if (response.ok) {
				setTags(data);
			} else {
				console.log('Error getting tags:', data.error);
			}
		} catch (error) {
			console.error('Error getting tags:', error);
		}
	};

	useEffect(() => {
		getTags();
	}, []);

	// Combined function to upload media and create tag
	const handleUploadMediaAndTag = async (event) => {
		event.preventDefault();

		if (!mediaFile) {
			alert('Please select a media file first!');
			return;
		}

		if (!tag) {
			alert('Please enter a tag name!');
			return;
		}

		setIsUploading(true);

		try {
			// Step 1: Upload media file
			const formData = new FormData();
			formData.append('media', mediaFile);

			const mediaResponse = await fetch(`${baseUrl}/api/v1/media/upload`, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${Cookie.get('token')}`,
				},
				body: formData,
			});

			if (!mediaResponse.ok) {
				const errorData = await mediaResponse.json();
				throw new Error(errorData.error || 'Media upload failed');
			}

			const mediaData = await mediaResponse.json();
			const newMediaId = mediaData.id; // Adjust this based on your API response structure

			// Step 2: Create tag with the uploaded media ID
			const tagResponse = await fetch(`${baseUrl}/api/v1/tags`, {
				method: 'POST',
				body: JSON.stringify({
					name: tag,
					mediaId: newMediaId,
				}),
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${Cookie.get('token')}`,
				},
			});

			if (tagResponse.ok) {
				alert('Media and tag uploaded successfully!');

				// Clear the form
				setMediaFile(null);
				setTag('');
				setUploadedMediaId('');

				// Clear file input
				const fileInput = document.getElementById('media-file');
				if (fileInput) fileInput.value = '';

				// Refresh tags list
				getTags();
			} else {
				const errorData = await tagResponse.json();
				throw new Error(errorData.error || 'Tag creation failed');
			}
		} catch (error) {
			console.error('Error uploading media and tag:', error);
			alert(`Upload failed: ${error.message}`);
		} finally {
			setIsUploading(false);
		}
	};

	const handleDeleteTag = async (event) => {
		event.preventDefault();
		if (!deletedTagId) {
			alert('Please select a tag to delete!');
			return;
		}

		try {
			const response = await fetch(`${baseUrl}/api/v1/tags/${deletedTagId}`, {
				method: 'DELETE',
				headers: {
					Authorization: `Bearer ${Cookie.get('token')}`,
				},
			});

			if (response.ok) {
				setDeletedTagId('');
				getTags();
				alert('Tag deleted successfully!');
			} else {
				const data = await response.json();
				alert(
					'Tag deletion failed: ' + (data.error || 'Unknown error occurred.')
				);
			}
		} catch (error) {
			console.error('Error While deleting tag:', error);
			alert('Error While deleting tag!');
		}
	};

	// Function to handle file selection with preview (optional)
	const handleFileChange = (e) => {
		const file = e.target.files[0];
		setMediaFile(file);
	};

	return (
		<div className='flex justify-center h-auto gap-4 items-start flex-wrap p-4 mt-36'>
			{/* Combined Media and Tag Upload Form */}
			<form
				className='bg-white p-6 rounded-lg shadow-md w-full max-w-md border-2 border-main mb-4'
				onSubmit={handleUploadMediaAndTag}>
				<h2 className='text-2xl text-main font-bold mb-4'>{t('uploadTag')}</h2>

				{/* Media File Input */}
				<div className='mb-4'>
					<label className='block text-sm font-medium text-gray-700 mb-2'>
						Select Media File
					</label>
					<input
						id='media-file'
						type='file'
						accept='image/*,video/*'
						onChange={handleFileChange}
						className='block w-full text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none p-2'
					/>
					{mediaFile && (
						<p className='mt-1 text-sm text-green-600'>
							Selected: {mediaFile.name}
						</p>
					)}
				</div>

				{/* Tag Input */}
				<div className='mb-4'>
					<label className='block text-sm font-medium text-gray-700 mb-2'>
						Tag Name
					</label>
					<input
						type='text'
						value={tag}
						onChange={(event) => setTag(event.target.value)}
						placeholder='Enter tag name'
						className='block w-full text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none p-2'
					/>
				</div>

				<button
					type='submit'
					disabled={isUploading || !mediaFile || !tag}
					className={`w-full py-2 px-4 rounded-lg font-medium ${
						isUploading || !mediaFile || !tag
							? 'bg-gray-300 text-gray-500 cursor-not-allowed'
							: 'bg-main text-white hover:bg-blue-700'
					}`}>
					{isUploading ? 'Uploading...' : t('upload')}
				</button>
			</form>

			{/* Tag Deletion Form */}
			{tags.length > 0 ? (
				<form
					className='bg-white p-6 rounded-lg shadow-md w-full max-w-md border-2 border-red-600 mb-4'
					onSubmit={handleDeleteTag}>
					<h2 className='text-2xl text-red-600 font-bold mb-4'>Delete Tag</h2>
					<select
						className='block w-full text-sm text-red-600 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none mb-4 p-2'
						value={deletedTagId}
						onChange={(e) => setDeletedTagId(e.target.value)}>
						<option value=''>Select a tag to delete</option>
						{tags.map((tag) => (
							<option
								key={tag.id}
								value={tag.id}>
								{tag.name}
							</option>
						))}
					</select>
					<button
						type='submit'
						className='w-full bg-red-700 text-white py-2 px-4 rounded-lg hover:bg-red-800'>
						Delete
					</button>
				</form>
			) : (
				<div className='w-full max-w-md'>
					<h2 className='text-2xl text-main font-bold text-center'>
						{t('noTags')}
					</h2>
				</div>
			)}
		</div>
	);
};

export default AddTags;
